/**
 * Paged MobX store for the redesigned Manage Returns list: pagination, list rows,
 * `status_group` / `include_tab_counts`, cached `tab_counts` for tab labels, and the
 * date column sort state (descending by default).
 * See ai-docs/manage-returns-redesign.md Section 6.
 */
// eslint-disable-next-line
import { computed, makeObservable, observable, runInAction } from "mobx";
import { SORT_TYPES } from "../../../../components/table/table.types";
import dashboardTranslationsStore from "../../../../dashboard-translation/stores/dashboard-translations-store";
import PagedDataStore, { IFetchListArgs } from "../../../../stores/abstract-paged-data-store";
import RmaListItemStore, { getListItemCustomerId } from "../../rma-list-page/stores/rma-item-store";
import RmaListFiltersStore from "../../rma-list-page/stores/rma-list-filters-store";
import RmaListService, { IFetchRmaListArgs, IRmaTabCounts, MANAGE_RETURNS_BACKEND } from "../../../../services/api/rma-list-service";
import { TYPE, rootContainer } from "../../../../services/container/root";
import rmaPageMainStore from "../../rma-page/stores/rma-page-main-store";
import { IIframeService } from "../../../../services/iframe-service/iframe-service";
import customersMainStore from "../../../create-rma/stores/customers-main-store";
import { IRmaListItem } from "../../../../services/api/rma-list-service";
import { RmaBulkSelectionStore } from "./rma-bulk-selection-store";

/** Sortable date column on the list endpoint; the shown date depends on the UI date-field setting. */
export const DATE_SORT_FIELD = "display_date";

export interface IDateColumnSortOption {
    id: SORT_TYPES;
    label: string;
}

const TABLE_SETTINGS_SCOPE = "dashboard-returns-table-settings";

class RmaTabsPagedListStore extends PagedDataStore<RmaListItemStore> {
    @observable private _rmaListFiltersStore: RmaListFiltersStore = new RmaListFiltersStore({
        defaultDateRangeMonths: 3,
        backend: MANAGE_RETURNS_BACKEND.RMS_API,
    });
    private readonly _bulkSelectionStore = new RmaBulkSelectionStore(this._rmaListFiltersStore);
    @observable private _isLoaded: boolean = false;
    /** Cached Open / Closed / All counts for tab labels; updated when the API returns `tab_counts`. */
    @observable private _tabCounts: IRmaTabCounts | null = null;
    /**
     * Set for the duration of a bucket-triggered reload, which must carry and commit the bucket keys
     * alone — see `RmaListFiltersStore.getFiltersForBucketApply`.
     */
    @observable private _isBucketApply: boolean = false;

    constructor() {
        super();
        makeObservable(this);
        this._sortBy = DATE_SORT_FIELD;
        /** default sort direction is descending */
        this._sortDir = SORT_TYPES.DESC;
        rmaPageMainStore.setRedesignNavigationListProvider(() => this.list);
    }

    readonly perPage: number = 20;

    protected get _rmaListService(): RmaListService {
        return rootContainer.get<RmaListService>(TYPE.RmaListService);
    }

    private get _iframeService(): IIframeService {
        return rootContainer.get<IIframeService>(TYPE.IframService);
    }

    get rmaListFiltersStore() {
        return this._rmaListFiltersStore;
    }

    get bulkSelectionStore() {
        return this._bulkSelectionStore;
    }

    @computed
    get tabCounts(): IRmaTabCounts | null {
        return this._tabCounts;
    }

    /** Asc / Desc choices for the date column settings popover. */
    public get sortOptions(): IDateColumnSortOption[] {
        return [
            {
                id: SORT_TYPES.ASC,
                label: dashboardTranslationsStore.getTranslation("SORT_ASCENDING", TABLE_SETTINGS_SCOPE),
            },
            {
                id: SORT_TYPES.DESC,
                label: dashboardTranslationsStore.getTranslation("SORT_DESCENDING", TABLE_SETTINGS_SCOPE),
            },
        ];
    }

    /** Immediate reload for a bucket toggle: applies and commits only the bucket selection. */
    public async applyBucketSelection(): Promise<void> {
        this._bulkSelectionStore.clear();
        runInAction(() => {
            this._isBucketApply = true;
        });
        try {
            await this.loadPage(1);
        } finally {
            runInAction(() => {
                this._isBucketApply = false;
            });
        }
    }

    protected async fetchList(args: IFetchListArgs) {
        try {
            /** Read before any await: the flag is cleared once `applyBucketSelection` resolves. */
            const isBucketApply = this._isBucketApply;
            if (!this._isLoaded) {
                await this._rmaListFiltersStore.ensureDefaultDateRangeTimeZone();
            }
            // NOTE: dead guard — `getUpdatedRmas()` returns an array, so `!array` is always false. If
            // fixed, exclude the bucket-apply path: `hasChanges()` covers bucket keys only by accident.
            if (this._isLoaded && !this._rmaListFiltersStore.hasChanges() && !rmaPageMainStore.getUpdatedRmas()) {
                return;
            }
            const filters = isBucketApply
                ? this._rmaListFiltersStore.getFiltersForBucketApply()
                : this._rmaListFiltersStore.getUpdatedModel();
            const rmaListArgs: IFetchRmaListArgs = {
                ...args,
                ...filters,
                include_tab_counts: true,
            } as IFetchRmaListArgs;
            const result = await this._rmaListService.fetchRmaList(MANAGE_RETURNS_BACKEND.RMS_API, rmaListArgs);
            if (isBucketApply) {
                this._rmaListFiltersStore.commitBucketSelection();
            } else {
                this._rmaListFiltersStore.commitChanges();
            }
            runInAction(() => {
                this._isLoaded = true;
                /** Keep the cached labels when a response carries no counts rather than clearing them. */
                if (result?.tab_counts) {
                    this._tabCounts = result.tab_counts;
                }
            });

            this._enrichCustomerNames(result.data);

            return {
                ...result,
                data: result.data.map(rma => new RmaListItemStore(rma, { enrichCustomerNames: true })),
            };
        } catch (err) {
            console.error(err);
        }
    }

    public async load() {
        /** handle top frame history changes */
        this._iframeService.navigateReturns({});

        /** if we are navigating from RMA page to the list and the list was not loaded - load and exit */
        if (!this._isLoaded) {
            await this.loadPage(this._page);
            return;
        }

        /** if list was rendered and then some rmas were updated before user navigated back to the list - reload it */
        const updatedRmaIds = rmaPageMainStore.getUpdatedRmas();
        if (updatedRmaIds.length > 0) {
            await this.loadPage(this._page);
            rmaPageMainStore.clearUpdatedRmaIds();
        }
    }

    public resetIsLoaded() {
        this._isLoaded = false;
    }

    /** Batch-fetch customer names for the current page: rms-api sends ids, not names. */
    private _enrichCustomerNames(items: IRmaListItem[]): void {
        const customerIds = collectUniqueCustomerIds(items, true);
        if (customerIds.length === 0) {
            return;
        }
        void customersMainStore.fetchCustomersByIds(customerIds);
    }
}

/** Unique non-empty customer ids from a list page payload. */
export function collectUniqueCustomerIds(items: IRmaListItem[], useCustomerNameAsId: boolean): string[] {
    const seen = new Set<string>();
    const ids: string[] = [];
    for (const item of items) {
        const id = getListItemCustomerId(item, useCustomerNameAsId);
        if (id && !seen.has(id)) {
            seen.add(id);
            ids.push(id);
        }
    }
    return ids;
}

export default new RmaTabsPagedListStore();
